import cn from 'classnames';

import { useDictionary } from 'src/hooks/useDictionary';
import { useScrollToSearchResultsOnActiveFilterChange } from 'src/hooks/useSearchPage';

import ActiveFiltersList from 'components/BaseHelpers/ActiveFiltersList';
import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import SearchFilterAccordions from 'components/BaseHelpers/SearchFilterAccordions';
import SortByControl from 'components/BaseHelpers/SortByControl';
import StartIndex from 'components/BaseHelpers/StartIndex';
import StopIndex from 'components/BaseHelpers/StopIndex';
import Text from 'components/BaseHelpers/Text';
import TwoColumnLayout from 'components/BaseHelpers/TwoColumnLayout';

import type { ComponentProps } from '../../../types/component-props';
import type { CoreNewsInsightsSearchData } from '../../../types/components';
import CoreCrossLinksGrid from '../CoreCrossLinksGrid';
import NewsInsightsSearchCombobox from './components/NewsInsightsSearchCombobox';
import NewsInsightsSearchResultsSummary from './components/NewsInsightsSearchResultsSummary';
import ResultsListing from './components/ResultsListing';
import {
  NewsInsightsSearchListKey,
  newsInsightsSearchSortOptions,
  NewsInsightsSearchSortValue,
  useNewsInsightsSearch,
} from './useNewsInsightsSearch';

import styles from './CoreNewsInsightsSearch.module.scss';

export type CoreNewsInsightsSearchProps = ComponentProps & CoreNewsInsightsSearchData;

const CoreNewsInsightsSearch = (props: CoreNewsInsightsSearchProps): JSX.Element => {
  const {
    filters,
    keywordSearchPlaceholder,
    typeaheadResultsCount,
    blogCrossLinksResultsPerPage,
    newsInsightsBlogsResultsPerPage,
    eventsResultsPerPage,
    errorMessage,
    noResultsMessage,
    pinnedNewsInsightsBlogs,
    pinnedEvents,
    blogCrossLinksSectionHeading,
    newsInsightsBlogsSectionHeading,
    eventsSectionHeading,
    blogCrossLinks,
    initialFacetState,
  } = props.fields;

  const {
    filtersLabel,
    computeResultsCountLabel,
    queryFilterLabel,
    serviceFilterLabel,
    clearAllLabel,
    recentSearchesLabel,
    loadMoreLabel,
  } = useDictionary();

  const search = useNewsInsightsSearch({
    resultsPerPage: {
      newsinsightsblogs: newsInsightsBlogsResultsPerPage,
      events: eventsResultsPerPage,
    },
    filters,
    initialFacetState,
  });

  const sectionDefinitions: Array<{ key: NewsInsightsSearchListKey; title: string }> = [
    { key: 'newsinsightsblogs', title: newsInsightsBlogsSectionHeading },
    { key: 'events', title: eventsSectionHeading },
  ];

  const resultSections = sectionDefinitions.map(({ key, title }) => ({
    key,
    title,
    list: search.lists[key],
  }));

  const totalVisibleResults = resultSections.reduce((count, section) => {
    return count + section.list.items.length;
  }, 0);
  const hasAnyItems = totalVisibleResults > 0;
  const hasAnyError = resultSections.some((section) => Boolean(section.list.error));
  const hasLoadedInitialResults = resultSections.every((section) => section.list.hasLoaded);
  const isLoadingInitialResults =
    totalVisibleResults === 0 &&
    (!hasLoadedInitialResults || resultSections.some((section) => section.list.loading));
  const showDefaultState =
    search.hasHydratedFromUrl && !search.hasActiveFilters && search.query.length === 0;
  const resultsRef = useScrollToSearchResultsOnActiveFilterChange(search.activeFilters);

  return (
    <>
      <StopIndex />
      <section className={cn(styles.main, 'container-12')}>
        <TwoColumnLayout
          leftChildren={
            <div className={styles['left-column']}>
              <NewsInsightsSearchCombobox
                placeholder={keywordSearchPlaceholder}
                queryDraft={search.queryDraft}
                typeaheadResultsCount={typeaheadResultsCount}
                recentSearchesLabel={recentSearchesLabel}
                setQueryDraft={search.setQueryDraft}
                submitQuery={search.submitQuery}
              />
              {search.query && (
                <SortByControl
                  onChange={(nextSort) => {
                    for (const list of Object.values(search.lists)) {
                      const defaultSort = search.query ? 'relevance' : 'date';
                      list.setSort?.(nextSort === defaultSort ? null : nextSort);
                    }
                  }}
                  value={
                    search.lists.newsinsightsblogs.sort ??
                    ((search.query ? 'relevance' : 'date') as NewsInsightsSearchSortValue)
                  }
                  sortOptions={newsInsightsSearchSortOptions}
                />
              )}
              <SearchFilterAccordions
                filterConfigs={filters}
                filterState={search.filters}
                sectionLabel={filtersLabel}
                servicesFilterName="relatedservices"
                facetState={search.facetState}
              />
            </div>
          }
          rightChildren={
            <div ref={resultsRef} className={styles['right-column']}>
              {search.hasActiveFilters && (
                <>
                  <NewsInsightsSearchResultsSummary
                    lists={search.lists}
                    labels={{
                      newsinsightsblogs: newsInsightsBlogsSectionHeading,
                      events: eventsSectionHeading,
                    }}
                  />
                  <ActiveFiltersList
                    labels={{
                      query: queryFilterLabel,
                      contenttypes: search.filters.contenttypes.label,
                      relatedservices: serviceFilterLabel,
                      'clear-all': clearAllLabel,
                    }}
                    activeFilters={search.activeFilters}
                    clearAll={search.clearAll}
                  />
                </>
              )}

              {showDefaultState && blogCrossLinks.length > 0 && (
                <CoreCrossLinksGrid
                  fields={{
                    heading: blogCrossLinksSectionHeading,
                    links: blogCrossLinks,
                    description: '',
                    displayCount: blogCrossLinksResultsPerPage,
                  }}
                  componentName="CoreCrossLinksGrid"
                />
              )}

              {Boolean(hasAnyError) && !hasAnyItems && (
                <RichText className="body-sm" field={errorMessage} />
              )}

              {hasLoadedInitialResults &&
                !isLoadingInitialResults &&
                !hasAnyItems &&
                !hasAnyError && <RichText className="body-sm" field={noResultsMessage} />}

              {(hasAnyItems || hasAnyError || isLoadingInitialResults) && (
                <div className={styles.sections}>
                  {resultSections.map(({ key, title, list }) => {
                    const pinnedItems =
                      key === 'newsinsightsblogs' ? pinnedNewsInsightsBlogs : pinnedEvents;
                    const hasItems = list.items.length !== 0 || pinnedItems.length !== 0;
                    if (!hasItems && !list.error) {
                      return null;
                    }
                    return (
                      <div className={styles.section} key={key} data-section={key}>
                        <div className={styles['section-header']}>
                          <div>
                            <Text
                              tag="h2"
                              className={cn('heading-h4', styles['section-title'])}
                              field={title}
                            />
                          </div>
                          {search.hasActiveFilters && (
                            <div className={cn('body-sm', styles['section-meta'])}>
                              {computeResultsCountLabel(
                                list.items.length,
                                list.totalCount ?? list.items.length
                              )}
                            </div>
                          )}
                        </div>

                        {Boolean(list.error) && list.items.length === 0 && (
                          <RichText className="body-sm" field={errorMessage} />
                        )}

                        {!list.loading && !list.error && !hasItems && hasAnyItems && (
                          <div className={cn('body-sm', styles['section-empty'])}>
                            No {title.toLowerCase()} matched your current filters.
                          </div>
                        )}
                        {list.items.length > 0 && (
                          <ResultsListing
                            items={list.items}
                            pinnedItems={
                              key === 'newsinsightsblogs' ? pinnedNewsInsightsBlogs : pinnedEvents
                            }
                            shouldShowPinnedItems={showDefaultState}
                            resultsToShow={list.take}
                          />
                        )}

                        {list.hasMore && (
                          <div>
                            <Cta
                              tag="button"
                              onClick={list.loadMore}
                              disabled={list.loading}
                              type="button"
                            >
                              {loadMoreLabel}
                            </Cta>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          }
        />
      </section>
      <StartIndex />
    </>
  );
};

export default CoreNewsInsightsSearch;
